// Admin → Role Mailboxes (2026-09-04): which shared mailboxes each role may open from Dispatch / the tech app.
// Backed by GET/PUT /api/workspace/role-mailboxes. A user with several roles gets the union; the role they are
// working in decides which boxes are offered first.
import { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Divider, Grid, IconButton, InputAdornment, MenuItem, Select,
  Snackbar, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Save as SaveIcon, Mail as MailIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { workspaceApi } from '../services/api';

type Role = { id: number; name: string };
type Item = { id?: number; role_id: number; mailbox: string; label?: string | null };

const KNOWN: { mailbox: string; label: string }[] = [
  { mailbox: 'tech@customer.concept.bz', label: 'Tech Mail' },
  { mailbox: 'inbox@customer.concept.bz', label: 'Office Inbox' },
];
const MAILBOX_RE = /^[a-z0-9._-]+@[a-z0-9.-]+$/i;

export default function RoleMailboxesSettings() {
  const [roles, setRoles] = useState<Role[]>([]);
  const [items, setItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [snack, setSnack] = useState<{ msg: string; sev: 'success' | 'error' } | null>(null);
  const [draft, setDraft] = useState<Record<number, { mailbox: string; label: string }>>({});

  const load = async () => {
    setLoading(true);
    try { const r = await workspaceApi.roleMailboxes(); setRoles(r.data.roles || []); setItems(r.data.items || []); setDirty(false); }
    catch (e: any) { setSnack({ msg: e?.response?.data?.detail || 'Could not load role mailboxes', sev: 'error' }); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const forRole = (rid: number) => items.filter((i) => i.role_id === rid);
  const add = (rid: number) => {
    const d = draft[rid] || { mailbox: '', label: '' };
    const mb = d.mailbox.trim().toLowerCase();
    if (!MAILBOX_RE.test(mb)) { setSnack({ msg: 'Enter a full mailbox address, e.g. billing@customer.concept.bz', sev: 'error' }); return; }
    if (forRole(rid).some((i) => i.mailbox === mb)) return;
    const known = KNOWN.find((k) => k.mailbox === mb);
    setItems([...items, { role_id: rid, mailbox: mb, label: d.label.trim() || known?.label || mb.split('@')[0] }]);
    setDraft({ ...draft, [rid]: { mailbox: '', label: '' } }); setDirty(true);
  };
  const remove = (rid: number, mb: string) => { setItems(items.filter((i) => !(i.role_id === rid && i.mailbox === mb))); setDirty(true); };
  const save = async () => {
    setSaving(true);
    try {
      const r = await workspaceApi.saveRoleMailboxes(items.map((i) => ({ role_id: i.role_id, mailbox: i.mailbox, label: i.label || null })));
      setItems(r.data.items || []); setDirty(false); setSnack({ msg: 'Role mailboxes saved', sev: 'success' });
    } catch (e: any) { setSnack({ msg: e?.response?.data?.detail || 'Save failed', sev: 'error' }); }
    finally { setSaving(false); }
  };

  if (loading) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <MailIcon sx={{ color: '#1E4A6B' }} />
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b', flex: 1 }}>Role mailboxes</Typography>
        <Button startIcon={<RefreshIcon />} onClick={load} disabled={saving}>Reload</Button>
        <Button variant="contained" startIcon={<SaveIcon />} onClick={save} disabled={!dirty || saving}
          sx={{ background: 'linear-gradient(180deg,#FF4A4A,#E30613 55%,#B00010)', fontWeight: 700 }}>Save changes</Button>
      </Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        Each role lists the shared mailboxes it may open from the Mail button (desktop and tech app). A person with several roles gets all of them;
        the role they are working in decides which boxes are offered first. The mailbox must exist on the mail server
        (files host, <code>secrets/mailboxes</code>) for the one-click sign-in to work.
      </Alert>
      <Grid container spacing={2}>
        {roles.map((r) => {
          const d = draft[r.id] || { mailbox: '', label: '' };
          return (
            <Grid item xs={12} md={6} key={r.id}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1E4A6B' }}>{r.name}</Typography>
                  <Divider sx={{ my: 1 }} />
                  <Table size="small">
                    <TableHead><TableRow><TableCell>Mailbox</TableCell><TableCell>Label</TableCell><TableCell align="right" /></TableRow></TableHead>
                    <TableBody>
                      {forRole(r.id).length === 0 && <TableRow><TableCell colSpan={3} sx={{ color: 'text.secondary' }}>No shared mailbox for this role</TableCell></TableRow>}
                      {forRole(r.id).map((i) => (
                        <TableRow key={i.mailbox}>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: 13 }}>{i.mailbox}</TableCell>
                          <TableCell><Chip size="small" label={i.label || i.mailbox.split('@')[0]} /></TableCell>
                          <TableCell align="right"><IconButton size="small" onClick={() => remove(r.id, i.mailbox)} aria-label="Remove"><DeleteIcon fontSize="small" /></IconButton></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
                    <Select size="small" displayEmpty value="" onChange={(e) => { const k = KNOWN.find((x) => x.mailbox === e.target.value); if (k) setDraft({ ...draft, [r.id]: { mailbox: k.mailbox, label: k.label } }); }} sx={{ minWidth: 150 }}>
                      <MenuItem value="" disabled>Pick…</MenuItem>
                      {KNOWN.map((k) => <MenuItem key={k.mailbox} value={k.mailbox}>{k.label} — {k.mailbox}</MenuItem>)}
                    </Select>
                    <TextField size="small" placeholder="mailbox@customer.concept.bz" value={d.mailbox} onChange={(e) => setDraft({ ...draft, [r.id]: { ...d, mailbox: e.target.value } })}
                      sx={{ flex: 1, minWidth: 200 }} InputProps={{ startAdornment: <InputAdornment position="start"><MailIcon fontSize="small" /></InputAdornment> }} />
                    <TextField size="small" placeholder="Label" value={d.label} onChange={(e) => setDraft({ ...draft, [r.id]: { ...d, label: e.target.value } })} sx={{ width: 130 }} />
                    <Button variant="outlined" startIcon={<AddIcon />} onClick={() => add(r.id)}>Add</Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
      <Snackbar open={!!snack} autoHideDuration={4000} onClose={() => setSnack(null)}>
        <Alert severity={snack?.sev || 'success'} onClose={() => setSnack(null)}>{snack?.msg}</Alert>
      </Snackbar>
    </Box>
  );
}
